import { PAYMENT_PROPERTY, PAYMENT_PROVIDERS } from "../../payment/index.js";
import { SUBSCRIPTION_CONTRACT_META_KEY, CustomerType, CUSTOMER_EXTERNAL_REFERENCE_KEY, CUSTOMER_METADATA, CustomerStatusReason, CustomerStatus, } from "../../index.js";
import { isMoreThanOneHourUntil } from "../../utils.js";
import Logger from "@cappelendamm/logger";
export const getBookSkuFromSeriesIndex = (books, selectedBookNo) => {
    const bookNo = selectedBookNo ?? 1;
    const selectedBookIdx = bookNo - 1;
    const selectedBookSku = books?.[selectedBookIdx]?.sku;
    return selectedBookSku;
};
/**
 * Find the subscription plan period in weeks from the import, if any.
 * We assume interval has the unit of weeks.
 * @param importedContract
 * @param subscriptionProduct
 */
const findSubscriptionPlanPeriod = (importedContract, subscriptionProduct) => {
    if (isNaN(Number(importedContract.interval))) {
        return null;
    }
    return subscriptionProduct.plan.periods.find((period) => {
        return (period.recurring?.period === Number(importedContract.interval) &&
            period.recurring?.unit === "week");
    });
};
export const getContractData = (importedContract, subscriptionProduct) => {
    const phone = importedContract.phone_number ?? undefined;
    const deliveryAddress = {
        phone,
        firstName: importedContract.delivery_first_name ||
            importedContract.billing_first_name ||
            importedContract.first_name,
        lastName: importedContract.delivery_last_name ||
            importedContract.billing_last_name ||
            importedContract.last_name,
        email: importedContract.email,
        street: importedContract.delivery_address,
        postalCode: importedContract.delivery_zip_code,
        city: importedContract.delivery_city,
        country: "NORGE",
    };
    const billingAddress = {
        phone,
        firstName: importedContract.billing_first_name ||
            importedContract.delivery_first_name ||
            importedContract.first_name,
        lastName: importedContract.billing_last_name ||
            importedContract.delivery_last_name ||
            importedContract.last_name,
        email: importedContract.email,
        street: importedContract.billing_address || importedContract.delivery_address,
        postalCode: importedContract.billing_zip_code || importedContract.delivery_zip_code,
        city: importedContract.billing_city || importedContract.delivery_city,
        country: "NORGE",
    };
    const externalReferences = [
        ...(importedContract.kindle_email &&
            String(importedContract.kindle_email).trim() !== ""
            ? [
                {
                    key: CUSTOMER_EXTERNAL_REFERENCE_KEY.KINDLE_EMAIL,
                    value: String(importedContract.kindle_email).trim().toLowerCase(),
                },
            ]
            : []),
        ...(importedContract.dds_id && String(importedContract.dds_id).trim() !== ""
            ? [
                {
                    key: CUSTOMER_EXTERNAL_REFERENCE_KEY.DDS_ID,
                    value: String(importedContract.dds_id).trim(),
                },
            ]
            : []),
        ...(importedContract.data_factory_id &&
            String(importedContract.data_factory_id).trim() !== ""
            ? [
                {
                    key: CUSTOMER_EXTERNAL_REFERENCE_KEY.DATA_FACTORY_ID,
                    value: String(importedContract.data_factory_id).trim(),
                },
            ]
            : []),
        ...(importedContract.salto_customer_id &&
            String(importedContract.salto_customer_id).trim() !== ""
            ? [
                {
                    key: CUSTOMER_EXTERNAL_REFERENCE_KEY.SALTO_CUSTOMER_ID,
                    value: String(importedContract.salto_customer_id).trim(),
                },
            ]
            : []),
        ...(importedContract.salto_customer_florida_id &&
            String(importedContract.salto_customer_florida_id).trim() !== ""
            ? [
                {
                    key: CUSTOMER_EXTERNAL_REFERENCE_KEY.SALTO_CUSTOMER_FLORIDA_ID,
                    value: String(importedContract.salto_customer_florida_id).trim(),
                },
            ]
            : []),
    ];
    const customerMeta = [
        ...(importedContract.verified_email &&
            String(importedContract.verified_email).trim() !== ""
            ? [
                {
                    key: CUSTOMER_METADATA.VERIFIED_EMAIL,
                    value: String(importedContract.verified_email).trim().toLowerCase(),
                },
            ]
            : []),
        ...(importedContract.salto_debt_status
            ? [
                {
                    key: CUSTOMER_METADATA.SALTO_DEBT_STATUS,
                    value: importedContract.salto_debt_status, // Either "ACTIVE" or not set
                },
            ]
            : []),
        ...(importedContract.salto_debt_status === "ACTIVE" // If in debt collection, suspend customer by setting status and reason
            ? [
                {
                    key: CUSTOMER_METADATA.CUSTOMER_STATUS,
                    value: CustomerStatus.SUSPENDED,
                },
                {
                    key: CUSTOMER_METADATA.CUSTOMER_STATUS_REASON,
                    value: CustomerStatusReason.DEBT_COLLECTION,
                },
            ]
            : []),
    ];
    const customer = {
        type: CustomerType.INDIVIDUAL,
        firstName: importedContract.first_name,
        lastName: importedContract.last_name,
        birthDate: importedContract.birth_date,
        email: importedContract.email,
        phone,
        addresses: {
            delivery: deliveryAddress,
            billing: billingAddress,
        },
        externalReferences,
        meta: customerMeta,
    };
    const paymentMethod = {
        [PAYMENT_PROPERTY.SHARED.IDENTIFIER]: PAYMENT_PROVIDERS.INVOICE,
    };
    const product = {
        sku: importedContract.sku,
        planIdentifier: subscriptionProduct.plan.identifier,
        subscriptionPlanPeriodId: findSubscriptionPlanPeriod(importedContract, subscriptionProduct)?.id,
    };
    const gifts = {
        skus: importedContract.selected_skus ??
            subscriptionProduct.gifts?.items.map(({ sku }) => sku) ??
            [],
    };
    const freight = {
        initialSku: subscriptionProduct.freight.initial[0]?.sku,
        recurringSku: subscriptionProduct.freight.recurring[0]?.sku,
    };
    const isCampaignBooksAvailable = subscriptionProduct.campaignBooks &&
        subscriptionProduct.campaignBooks.length > 0;
    const meta = {
        [SUBSCRIPTION_CONTRACT_META_KEY.SOURCE]: importedContract.source,
        ...(importedContract.company && {
            [SUBSCRIPTION_CONTRACT_META_KEY.COMPANY]: importedContract.company,
        }),
        ...(importedContract.campaign_code && {
            [SUBSCRIPTION_CONTRACT_META_KEY.CAMPAIGN_SOURCE]: importedContract.campaign_code,
        }),
        ...(importedContract.distribution_method && {
            [SUBSCRIPTION_CONTRACT_META_KEY.DISTRIBUTION_METHOD]: importedContract.distribution_method,
        }),
        ...(importedContract.distribution_media && {
            [SUBSCRIPTION_CONTRACT_META_KEY.DISTRIBUTION_MEDIA]: importedContract.distribution_media,
        }),
        ...(importedContract.salto_subscription_id && {
            [SUBSCRIPTION_CONTRACT_META_KEY.SALTO_CONTRACT_ID]: importedContract.salto_subscription_id,
        }),
        ...(gifts.skus.length > 0 && {
            [SUBSCRIPTION_CONTRACT_META_KEY.GIFTS]: gifts.skus.join(","),
        }),
        [SUBSCRIPTION_CONTRACT_META_KEY.SALTO_CREATED_AT]: importedContract.salto_created_at,
        ...(!isCampaignBooksAvailable && {
            [SUBSCRIPTION_CONTRACT_META_KEY.INITIAL_SKU]: importedContract.next_isbn,
        }),
        ...(isCampaignBooksAvailable && {
            [SUBSCRIPTION_CONTRACT_META_KEY.CAMPAIGN_BOOKS]: subscriptionProduct.campaignBooks.map((book) => book.sku).join(","),
        }),
    };
    const status = getContractStatusRenew(importedContract, subscriptionProduct);
    return {
        customer,
        product,
        paymentMethod,
        freight,
        meta,
        status,
    };
};
const getContractStatusRenew = (importedContract, subscriptionProduct) => {
    Logger.log("Getting contract status for imported contract.");
    if (!subscriptionProduct.isSeries) {
        Logger.log("Contract is not a series, will not set status.");
        return undefined;
    }
    // For series subscriptions with progress, should renew to wanted date if possible
    if (importedContract.renew_at &&
        isMoreThanOneHourUntil(new Date(importedContract.renew_at), new Date())) {
        Logger.log("Imported contract has a renewAt date in the future, will set status.");
        return { renewAt: importedContract.renew_at };
    }
    Logger.log("Imported contract does not have a renewAt date in the future, will not set status.");
    return undefined;
};
